import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { BRAND, TOPICS } from "@/content";
import { submitSpeaking } from "@/api";

const initial = {
  name: "", email: "", organization: "", phone: "",
  event_date: "", event_type: "", audience_size: "", budget: "", message: "",
};

export default function Speaking() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setSending(true);
    try {
      await submitSpeaking(form);
      setDone(true);
      setForm(initial);
      toast.success("Inquiry sent. Crystal's team will reply within 2 business days.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email crystalpurdue29@gmail.com.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-speaking" className="bg-white">
      <section className="pt-[120px] pb-16">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p className="overline">Speaking</p>
            <h1 className="display mt-6 text-[44px] md:text-[120px]">
              Book the keynote<br/>your room <em className="font-serif text-[var(--ic-accent)]">won't forget</em>.
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <p className="font-sans text-[17px] leading-[1.75] text-black/75">
              I speak to women's gatherings, schools, churches, wellness conferences and
              corporate events that want more than another talk &mdash; they want a shift
              in the room.
            </p>
          </div>
        </div>
      </section>

      <section className="hairline-b">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
          <div className="zoom-img-wrap relative overflow-hidden border border-[var(--ic-line)] aspect-[16/9] bg-[var(--ic-warm)]">
            <img src={BRAND.images.speaking} alt="Crystal Purdue speaking" className="zoom-img absolute inset-0 w-full h-full object-cover object-[center_30%]" />
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="overline">Signature topics</p>
            <h2 className="section-display mt-4">Four talks.<br/>One movement.</h2>
          </div>
          <ul className="col-span-12 lg:col-span-8 divide-y divide-[var(--ic-line)] border-t border-b border-[var(--ic-line)]">
            {TOPICS.map((t, i) => (
              <li key={i} className="grid grid-cols-12 gap-6 py-8" data-testid={`topic-${i}`}>
                <span className="col-span-2 overline pt-2">0{i+1}</span>
                <div className="col-span-10">
                  <h3 className="font-serif text-[28px] md:text-[34px] leading-tight">{t.title}</h3>
                  <p className="font-sans text-[15.5px] text-black/70 mt-3 max-w-2xl">{t.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-[var(--ic-warm)] py-24 lg:py-32 border-y border-[var(--ic-line)]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="overline">Audience outcomes</p>
            <h2 className="section-display mt-4">What rooms leave with.</h2>
          </div>
          <ul className="col-span-12 lg:col-span-8 space-y-5">
            {[
              "Permission to stop apologizing for wanting more",
              "Language for what they've been feeling but couldn't name",
              "A clear next choice — not a vague resolution",
              "A renewed sense of agency they bring back to their work, family, and self",
            ].map((o, i) => (
              <li key={i} className="flex gap-4 items-start">
                <CheckCircle2 size={20} className="mt-1 text-black" />
                <span className="font-sans text-[17px] leading-[1.6] text-black/85">{o}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="inquiry" className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="overline">Speaking inquiry</p>
            <h2 className="section-display mt-4">Book Crystal<br/>to speak.</h2>
            <p className="font-sans text-[15px] text-black/65 mt-6 max-w-sm">
              Tell us about your event. We typically reply within two business days with
              availability, topic options, and fee details.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-8" data-testid="speaking-form-wrap">
            {done ? (
              <div className="border border-[var(--ic-line)] p-10" data-testid="speaking-success">
                <p className="overline">Inquiry received</p>
                <h3 className="font-serif text-[34px] mt-4">Thank you for inviting Crystal.</h3>
                <p className="font-sans text-[16px] text-black/70 mt-4 max-w-xl">
                  Your inquiry is in. Crystal's team will personally review it and follow up within
                  two business days. A confirmation email is on its way to your inbox.
                </p>
                <button className="ed-link mt-8" onClick={() => setDone(false)}>Submit another inquiry</button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-12 gap-6" data-testid="speaking-form">
                <FieldText col={6} label="Name *"          v={form.name} on={onChange("name")} testid="sp-name"/>
                <FieldText col={6} label="Email *"         v={form.email} on={onChange("email")} type="email" testid="sp-email"/>
                <FieldText col={6} label="Organization"    v={form.organization} on={onChange("organization")} testid="sp-org"/>
                <FieldText col={6} label="Phone"           v={form.phone} on={onChange("phone")} testid="sp-phone"/>
                <FieldText col={6} label="Event date"      v={form.event_date} on={onChange("event_date")} testid="sp-date"/>
                <FieldText col={6} label="Event type"      v={form.event_type} on={onChange("event_type")} placeholder="Keynote, panel, workshop…" testid="sp-type"/>
                <FieldText col={6} label="Audience size"   v={form.audience_size} on={onChange("audience_size")} testid="sp-size"/>
                <FieldText col={6} label="Budget range"    v={form.budget} on={onChange("budget")} testid="sp-budget"/>
                <FieldArea col={12} label="Tell us about the event *" v={form.message} on={onChange("message")} testid="sp-message"/>

                <div className="col-span-12 flex flex-wrap items-center gap-4 mt-2">
                  <button type="submit" disabled={sending} data-testid="sp-submit" className="btn-ink disabled:opacity-50">
                    {sending ? "Sending…" : "Send Speaking Inquiry"}
                  </button>
                  <span className="text-[12px] text-black/55">We respect your time. No spam, ever.</span>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function FieldText({ col=12, label, v, on, type="text", placeholder="", testid }) {
  return (
    <div className={`col-span-12 md:col-span-${col}`}>
      <label className="ed-label block">{label}</label>
      <input data-testid={testid} type={type} value={v} onChange={on} placeholder={placeholder} className="ed-input" />
    </div>
  );
}
function FieldArea({ col=12, label, v, on, testid }) {
  return (
    <div className={`col-span-12 md:col-span-${col}`}>
      <label className="ed-label block">{label}</label>
      <textarea data-testid={testid} value={v} onChange={on} className="ed-textarea" rows={5} />
    </div>
  );
}
